import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../i18n/LanguageContext";

const stripePattern = [
  "/images/support-accent-lime.svg",
  "/images/support-accent-teal.svg",
  "/images/support-accent-lime.svg",
  "/images/support-accent-teal.svg",
  "/images/support-accent-lime.svg",
  "/images/support-accent-teal.svg",
  "/images/support-accent-lime.svg",
  "/images/support-accent-teal.svg",
  "/images/support-accent-lime.svg",
  "/images/support-accent-teal.svg",
  "/images/support-accent-lime.svg",
  "/images/support-accent-teal.svg",
  "/images/support-accent-lime.svg",
  "/images/support-accent-lime.svg",
  "/images/support-accent-lime.svg",
  "/images/support-accent-lime.svg",
  "/images/support-accent-lime.svg",
];

const patternRows = 4;
const patternColumns = 24;

const SupportIntroSection: React.FC = () => {
  const { t, dir } = useLanguage();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [imageOffset, setImageOffset] = useState(0);
  const isRtl = dir === "rtl";

  const content = (t as any)?.homeSupportIntro || {};
  const title =
    content.title || "A decision this big deserves\nthe right support.";
  const description =
    content.description ||
    "Moving your child to a new country to study is not a small thing. It takes planning, paperwork, and a lot of trust. We built EduVate because we know how stressful that process can be when you're doing it alone.";

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.18 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    let ticking = false;

    const updateImageOffset = () => {
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 0;
      const sectionCenter = rect.top + rect.height / 2;
      const viewportCenter = viewportHeight / 2;
      const distanceFromCenter = sectionCenter - viewportCenter;
      const maxOffset = 28;
      const normalizedOffset =
        viewportHeight > 0 ? distanceFromCenter / viewportHeight : 0;
      const nextOffset = Math.max(
        -maxOffset,
        Math.min(maxOffset, normalizedOffset * maxOffset)
      );

      setImageOffset(nextOffset);
      ticking = false;
    };

    const handleScroll = () => {
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(updateImageOffset);
    };

    updateImageOffset();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="overflow-hidden bg-white"
      aria-labelledby="support-intro-title"
    >
      <div
        className={`relative w-full overflow-hidden transition-all duration-700 ease-out ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
        }`}
      >
        <div className="relative min-h-screen w-full ">
          <div
            className="absolute inset-0 h-full w-full overflow-hidden"
            aria-hidden="true"
          >
            <div
              className="h-full w-full transition-transform duration-300 ease-out will-change-transform "
              style={{ transform: `translate3d(0, ${imageOffset}px, 0)` }}
            >
              <img
                src="/images/support-intro-graduate.jpg"
                alt=""
                className={`support-intro-image h-full w-full object-cover object-[center_36%] ${
                  isVisible ? "support-intro-image-visible" : ""
                }`}
              />
            </div>
          </div>

          <div className="relative z-10 flex min-h-screen items-end px-4 py-4 sm:px-6 sm:py-6 lg:px-6">
            <div className={`flex w-full ${isRtl ? "justify-start" : "justify-end"}`}>
              <div
                className={`w-full max-w-[620px] overflow-hidden bg-white transition-all duration-[950ms] ease-out ${
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"
                }`}
                style={{ transitionDelay: "120ms" }}
              >
                <div
                  className={`px-5 pt-5 sm:px-7 sm:pt-7 ${isRtl ? "text-right" : "text-left"}`}
                >
                  <div
                    className={`flex flex-col gap-3 ${
                      isRtl ? "items-end text-right" : "items-start text-left"
                    }`}
                  >
                    <h2
                      id="support-intro-title"
                      className="max-w-[440px] whitespace-pre-line font-sans text-2xl font-semibold leading-tight text-teal-900 sm:text-[32px]"
                    >
                      {title}
                    </h2>
                    <p className="max-w-[560px] font-sans text-sm font-normal leading-6 text-black sm:text-base">
                      {description}
                    </p>
                  </div>
                </div>

                <div
                  className="pointer-events-none mt-6 grid w-full gap-2 overflow-hidden px-3 pb-3 sm:mt-8 sm:gap-1.5 sm:px-0 sm:pb-1"
                  aria-hidden="true"
                  style={{
                    gridTemplateRows: `repeat(${patternRows}, minmax(0, 1fr))`,
                  }}
                >
                  {Array.from({ length: patternRows }).map((_, row) => (
                    <div
                      key={row}
                      className="grid w-full items-center gap-2 sm:gap-2.5"
                      style={{
                        gridTemplateColumns: `repeat(${patternColumns}, minmax(22px, 1fr))`,
                      }}
                    >
                      {Array.from({ length: patternColumns }).map((_, index) => (
                        <img
                          key={`${row}-${index}`}
                          src={stripePattern[index % stripePattern.length]}
                          alt=""
                          aria-hidden="true"
                          className={`support-pattern-cell h-auto w-full min-w-0 ${
                            isVisible ? "support-pattern-cell-visible" : ""
                          }`}
                          style={{
                            animationDelay: `${220 + row * 120 + index * 24}ms`,
                          }}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SupportIntroSection;